import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as DB from '@/lib/supabaseDb';
import { getInvoicesBySaleId, createInvoice, getElogoStatusLabel } from '@/lib/elogoDb';
import PaymentFormModal from '@/components/feature/PaymentFormModal';
import { formatTRY } from '@/lib/currencyFormat';
import { supabase } from '@/lib/supabase';
import Badge from '@/components/base/Badge';
import { paymentStatus, deliveryStatus, installmentStatus, elogoStatus } from '@/lib/statusDefs';

export default function SaleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showToast, setShowToast] = useState<string | null>(null);
  const [saleStatus, setSaleStatus] = useState<{ payment_status: string; delivery_status: string } | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showElogoModal, setShowElogoModal] = useState(false);
  const [elogoLoading, setElogoLoading] = useState(false);

  const [sale, setSale] = useState<any>(null);
  const [customer, setCustomer] = useState<any>(null);
  const [items, setItems] = useState<any[]>([]);
  const [installments, setInstallments] = useState<any[]>([]);
  const [delivery, setDelivery] = useState<any>(null);
  const [revisions, setRevisions] = useState<any[]>([]);
  const [loadingRevisions, setLoadingRevisions] = useState(false);
  const [showRevisions, setShowRevisions] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [elogoError, setElogoError] = useState<string | null>(null);
  const [elogoResult, setElogoResult] = useState<any>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      DB.getSaleById(id),
      DB.getSaleItems(id),
      DB.getInstallmentsBySaleId(id),
      DB.getDeliveriesByCustomerId(''),
      getInvoicesBySaleId(id),
    ]).then(([saleRes, itemsRes, instRes, _delRes, invRes]) => {
      if (cancelled) return;
      if (saleRes.error) { setError(saleRes.error); setLoading(false); return; }
      setSale(saleRes.data);
      setItems(itemsRes.data || []);
      setInstallments(instRes.data || []);
      if (!invRes.error && invRes.data) setInvoices(invRes.data);
      if (saleRes.data?.customer_id) {
        DB.getCustomerById(saleRes.data.customer_id).then(({ data }) => {
          if (!cancelled) setCustomer(data);
        });
      }
      if (saleRes.data?.id) {
        DB.getDeliveryBySaleId(saleRes.data.id).then(({ data }) => {
          if (!cancelled) setDelivery(data);
        });
      }
      setLoading(false);
    }).catch(() => { if (!cancelled) { setError('Veri yüklenirken hata oluştu.'); setLoading(false); } });
    return () => { cancelled = true; };
  }, [id]);

  const currentPaymentStatus = saleStatus?.payment_status || sale?.payment_status;
  const currentDeliveryStatus = saleStatus?.delivery_status || sale?.delivery_status;

  const handleCancel = async () => {
    setShowCancelModal(false);
    const { error: err } = await DB.updateSale(id!, { payment_status: 'İptal', delivery_status: 'İptal' });
    if (!err) {
      setSaleStatus({ payment_status: 'İptal', delivery_status: 'İptal' });
      setShowToast('Satış iptal edildi');
      setTimeout(() => setShowToast(null), 3000);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh]">
        <div className="w-12 h-12 rounded-full border-2 border-primary-200 border-t-primary-500 animate-spin mb-4"></div>
        <h2 className="text-lg font-semibold text-foreground-700">Yükleniyor...</h2>
      </div>
    );
  }

  if (error || !sale) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh]">
        <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mb-4"><i className="ri-file-unknow-line text-2xl text-foreground-300"></i></div>
        <h2 className="text-lg font-semibold text-foreground-700">{error || 'Satış bulunamadı'}</h2>
        <button onClick={() => navigate('/sales')} className="btn-primary mt-4 cursor-pointer">Listeye Dön</button>
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-4xl mx-auto">
      {showToast && <div className="fixed top-4 right-4 z-[100] bg-emerald-500 text-white px-5 py-3 rounded-xl shadow-lg flex items-center gap-2"><i className="ri-check-line"></i><span className="text-sm font-medium">{showToast}</span></div>}

      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/sales')} className="w-9 h-9 rounded-lg hover:bg-background-100 flex items-center justify-center cursor-pointer"><i className="ri-arrow-left-line text-foreground-600"></i></button>
          <div><div className="flex items-center gap-2 flex-wrap"><h2 className="text-xl font-bold text-foreground-900 font-heading">{sale.sale_no}</h2><Badge badgeClass={paymentStatus.badgeClass(currentPaymentStatus)} icon={paymentStatus.icon(currentPaymentStatus)}>{currentPaymentStatus}</Badge><Badge badgeClass={deliveryStatus.badgeClass(currentDeliveryStatus)} icon={deliveryStatus.icon(currentDeliveryStatus)}>{currentDeliveryStatus}</Badge></div><p className="text-sm text-foreground-400">{new Date(sale.sale_date).toLocaleDateString('tr-TR')} · {sale.created_by} tarafından oluşturuldu</p></div>
        </div>
        <div className="flex items-center gap-2 ml-auto flex-shrink-0 flex-wrap">
          <button onClick={() => navigate('/receipt/' + sale.id)} className="btn-outline flex items-center gap-2 text-sm cursor-pointer"><i className="ri-printer-line"></i> Makbuz</button>
          {currentPaymentStatus !== 'İptal' && <button onClick={() => navigate(`/sales/${sale.id}/edit`)} className="btn-outline flex items-center gap-2 text-sm cursor-pointer"><i className="ri-edit-line"></i> Düzenle</button>}
          {currentPaymentStatus !== 'İptal' && <button onClick={() => setShowElogoModal(true)} className="btn-outline flex items-center gap-2 text-sm cursor-pointer border-accent-300 text-accent-700 hover:bg-accent-50"><i className="ri-file-text-line"></i> eLogo Test Faturası</button>}
          {currentPaymentStatus !== 'İptal' && sale.remaining_amount > 0 && <button onClick={() => setShowPaymentModal(true)} className="btn-primary flex items-center gap-2 text-sm cursor-pointer"><i className="ri-bank-card-line"></i> Ödeme Al</button>}
          {currentPaymentStatus !== 'İptal' && <button onClick={() => setShowCancelModal(true)} className="btn-outline flex items-center gap-2 text-sm !text-red-500 !border-red-200 hover:!bg-red-50 cursor-pointer"><i className="ri-close-circle-line"></i> İptal</button>}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="stat-card"><span className="text-[11px] text-foreground-400 uppercase tracking-wider">Toplam Tutar</span><p className="text-lg font-bold text-foreground-900 mt-1">{formatTRY(sale.total_amount)}</p></div>
        <div className="stat-card"><span className="text-[11px] text-foreground-400 uppercase tracking-wider">Ödenen</span><p className="text-lg font-bold text-emerald-600 mt-1">{formatTRY(sale.paid_amount)}</p></div>
        <div className="stat-card"><span className="text-[11px] text-foreground-400 uppercase tracking-wider">Kalan Borç</span><p className={`text-lg font-bold mt-1 ${sale.remaining_amount > 0 ? 'text-red-500' : 'text-emerald-600'}`}>{sale.remaining_amount > 0 ? formatTRY(sale.remaining_amount) : '—'}</p></div>
        <div className="stat-card"><span className="text-[11px] text-foreground-400 uppercase tracking-wider">Peşinat</span><p className="text-lg font-bold text-foreground-900 mt-1">{formatTRY(sale.down_payment)}</p></div>
      </div>

      {customer && (
        <div className="bg-background-50 rounded-xl border border-background-200/70 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0"><i className="ri-user-3-line text-primary-600"></i></span>
              <div><button onClick={() => navigate(`/customers/${customer.id}`)} className="text-sm font-semibold text-foreground-800 hover:text-primary-500 cursor-pointer">{customer.full_name}</button><p className="text-xs text-foreground-400">{customer.phone}</p></div>
            </div>
            {customer.phone && <a href={`https://wa.me/90${customer.phone.replace(/\s/g, '').slice(1)}`} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center cursor-pointer hover:bg-emerald-100 transition-colors"><i className="ri-whatsapp-line text-emerald-600"></i></a>}
          </div>
        </div>
      )}

      <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden"><div className="px-5 py-3 border-b border-background-100 flex items-center justify-between"><h3 className="text-sm font-semibold text-foreground-800">Satılan Ürünler</h3><span className="text-xs text-foreground-400">{items.length} ürün</span></div><div className="overflow-x-auto"><table className="w-full min-w-[1000px]"><thead><tr className="border-b border-background-100"><th className="table-header">Ürün Cinsi</th><th className="table-header">Marka</th><th className="table-header">Ürün Adı</th><th className="table-header">Model Kod</th><th className="table-header">Renk</th><th className="table-header">Ölçü</th><th className="table-header text-right">Adet</th><th className="table-header text-right">Birim Fiyat</th><th className="table-header text-right">Birim Maliyet</th><th className="table-header text-right">Kâr</th><th className="table-header text-right">Toplam</th></tr></thead><tbody>{items.length > 0 ? items.map((item) => {
          const itemProfit = item.unit_cost_price != null ? (Number(item.unit_price) - Number(item.unit_cost_price)) * Number(item.quantity) : null;
          const itemProfitMargin = itemProfit != null && Number(item.unit_price) > 0 ? ((itemProfit / (Number(item.unit_price) * Number(item.quantity))) * 100).toFixed(1) : null;
          return (<tr key={item.id} className="border-b border-background-100"><td className="table-cell font-medium text-foreground-800">{item.product_type || '—'}</td><td className="table-cell text-foreground-500">{item.brand || '—'}</td><td className="table-cell font-medium text-foreground-800">{item.product_name || '—'}</td><td className="table-cell text-foreground-400 text-xs">{item.model_code || '—'}</td><td className="table-cell text-foreground-500">{item.color || '—'}</td><td className="table-cell text-foreground-500">{item.size || '—'}</td><td className="table-cell text-right">{item.quantity}</td><td className="table-cell text-right">{formatTRY(item.unit_price)}</td><td className="table-cell text-right">{item.unit_cost_price != null ? formatTRY(item.unit_cost_price) : <span className="text-amber-500 text-xs">Eksik</span>}</td><td className="table-cell text-right">{itemProfit != null ? <span className={itemProfit >= 0 ? 'text-emerald-600 font-medium' : 'text-red-500 font-medium'}>{formatTRY(itemProfit)}{itemProfitMargin != null && <span className="text-xs text-foreground-400 ml-1">(%{itemProfitMargin})</span>}</span> : <span className="text-amber-500 text-xs">—</span>}</td><td className="table-cell text-right font-medium">{formatTRY(item.total_price)}</td></tr>);
        }) : (<tr><td colSpan={11} className="table-cell text-center text-foreground-400 py-8"><i className="ri-information-line mr-1"></i>Bu satış için ürün satırları kaydedilmemiş.</td></tr>)}</tbody></table></div>
        {(() => {
          const totalProfit = items.reduce((s, i) => {
            if (i.unit_cost_price != null) return s + (Number(i.unit_price) - Number(i.unit_cost_price)) * Number(i.quantity);
            return s;
          }, 0);
          const totalCost = items.reduce((s, i) => {
            if (i.unit_cost_price != null) return s + Number(i.unit_cost_price) * Number(i.quantity);
            return s;
          }, 0);
          const unknownCostItems = items.filter(i => i.unit_cost_price == null);
          return (
            <div className="px-5 py-3 border-t border-background-200 bg-background-50">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-foreground-400">Toplam Maliyet:</span>
                  <span className="font-semibold text-foreground-700">{formatTRY(totalCost)}</span>
                  {unknownCostItems.length > 0 && <span className="text-xs text-amber-500">({unknownCostItems.length} ürün eksik)</span>}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-foreground-400">Brüt Kâr:</span>
                  <span className={`font-bold text-lg ${totalProfit >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{formatTRY(totalProfit)}</span>
                  {Number(sale.total_amount) > 0 && totalCost > 0 && (
                    <span className="text-xs text-foreground-400">(%{((totalProfit / Number(sale.total_amount)) * 100).toFixed(1)} marj)</span>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap sm:ml-auto">
                  {sale.discount_amount > 0 && <span className="text-foreground-400">İndirim: <span className="font-medium text-red-500">-{formatTRY(sale.discount_amount)}</span></span>}
                  {sale.shipping_fee > 0 && <span className="text-foreground-400">Nakliye: <span className="font-medium text-foreground-600">{formatTRY(sale.shipping_fee)}</span></span>}
                  {sale.extra_fee > 0 && <span className="text-foreground-400">Ek Ücret: <span className="font-medium text-foreground-600">{formatTRY(sale.extra_fee)}</span></span>}
                  <span className="text-foreground-400 font-semibold">Satış Toplamı:</span>
                  <span className="font-bold text-foreground-900">{formatTRY(sale.total_amount)}</span>
                </div>
              </div>
            </div>
          );
        })()}</div>

      {installments.length > 0 && (
        <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
          <div className="px-5 py-3 border-b border-background-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h3 className="text-sm font-semibold text-foreground-800">Taksit Planı</h3>
              {(installments[0]?.plan_type) && (
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${installments[0].plan_type === 'custom' ? 'bg-accent-100 text-accent-700' : 'bg-background-200 text-foreground-500'}`}>
                  {installments[0].plan_type === 'custom' ? 'Özel Plan' : 'Eşit Taksit'}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-foreground-400">{installments.length} taksit</span>
              <span className="text-xs text-foreground-400">|</span>
              <span className="text-xs text-foreground-400">
                Toplam: {formatTRY(installments.reduce((s, i) => s + (i.original_amount || i.amount || 0), 0))}
              </span>
              <span className="text-xs text-foreground-400">|</span>
              <span className="text-xs text-emerald-600">
                Ödenen: {formatTRY(installments.reduce((s, i) => s + (i.paid_amount || 0), 0))}
              </span>
              <span className="text-xs text-foreground-400">|</span>
              <span className="text-xs text-red-500">
                Kalan: {formatTRY(installments.reduce((s, i) => s + (i.remaining_amount !== undefined ? i.remaining_amount : (i.amount - (i.paid_amount || 0))), 0))}
              </span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead>
                <tr className="border-b border-background-100">
                  <th className="table-header">#</th>
                  <th className="table-header">Vade Tarihi</th>
                  <th className="table-header">Tutar</th>
                  <th className="table-header">Ödenen</th>
                  <th className="table-header">Kalan</th>
                  <th className="table-header">Durum</th>
                  <th className="table-header">Açıklama</th>
                </tr>
              </thead>
              <tbody>
                {(installments || []).sort((a, b) => (a.sequence_no || 0) - (b.sequence_no || 0)).map((inst) => {
                  const instRemaining = inst.remaining_amount !== undefined ? inst.remaining_amount : (inst.amount - (inst.paid_amount || 0));
                  const overdue = inst.status !== 'Ödendi' && inst.status !== 'İptal' && inst.due_date && inst.due_date < new Date().toISOString().split('T')[0] && instRemaining > 0;
                  return (
                    <tr key={inst.id} className={`border-b border-background-100 ${overdue ? 'bg-red-50/50' : ''}`}>
                      <td className="table-cell text-foreground-400">{inst.installment_no || inst.sequence_no || '?'}</td>
                      <td className="table-cell">{inst.due_date ? new Date(inst.due_date).toLocaleDateString('tr-TR') : '—'}</td>
                      <td className="table-cell font-medium">{formatTRY(inst.original_amount || inst.amount)}</td>
                      <td className="table-cell text-emerald-600">{inst.paid_amount > 0 ? formatTRY(inst.paid_amount) : '—'}</td>
                      <td className="table-cell">{instRemaining > 0 ? <span className="text-red-500 font-medium">{formatTRY(instRemaining)}</span> : <span className="text-emerald-600">—</span>}</td>
                      <td className="table-cell py-2"><Badge badgeClass={installmentStatus.badgeClass(overdue ? 'Gecikti' : inst.status)} icon={installmentStatus.icon(overdue ? 'Gecikti' : inst.status)}>{overdue ? 'Gecikti' : inst.status}</Badge></td>
                      <td className="table-cell text-foreground-400 text-xs max-w-[150px] truncate">{inst.description || inst.notes || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-background-50 rounded-xl border border-background-200/70 p-4"><h3 className="text-sm font-semibold text-foreground-800 mb-3">Teslimat Bilgisi</h3><div className="space-y-2.5 text-sm"><div className="flex justify-between"><span className="text-foreground-400">Durum</span><Badge badgeClass={deliveryStatus.badgeClass(currentDeliveryStatus)} icon={deliveryStatus.icon(currentDeliveryStatus)}>{currentDeliveryStatus}</Badge></div>{delivery ? (<><div className="flex justify-between"><span className="text-foreground-400">Tarih</span><span className="text-foreground-600">{delivery.delivery_date ? new Date(delivery.delivery_date + 'T00:00:00').toLocaleDateString('tr-TR') : '—'}</span></div><div className="flex justify-between"><span className="text-foreground-400">Saat</span><span className="text-foreground-600">{delivery.delivery_start_time || delivery.delivery_time || '—'}{delivery.delivery_end_time ? ` - ${delivery.delivery_end_time}` : ''}</span></div>{delivery.delivery_person && <div className="flex justify-between"><span className="text-foreground-400">Nakliyeci</span><span className="text-foreground-600">{delivery.delivery_person}{delivery.vehicle_plate ? ` (${delivery.vehicle_plate})` : ''}</span></div>}<div className="flex justify-between"><span className="text-foreground-400">İl / İlçe</span><span className="text-foreground-600 text-right max-w-[60%]">{[delivery.city, delivery.district].filter(Boolean).join(' / ') || '—'}</span></div><div className="flex justify-between"><span className="text-foreground-400">Adres</span><span className="text-foreground-600 text-right max-w-[60%]">{delivery.address || sale.delivery_address}</span></div>{delivery.notes && <div className="flex justify-between"><span className="text-foreground-400">Not</span><span className="text-foreground-500 text-right max-w-[60%] text-xs">{delivery.notes}</span></div>}</>) : (<div className="flex justify-between"><span className="text-foreground-400">Adres</span><span className="text-foreground-600 text-right max-w-[60%]">{sale.delivery_address}</span></div>)}</div></div>
        <div className="bg-background-50 rounded-xl border border-background-200/70 p-4"><h3 className="text-sm font-semibold text-foreground-800 mb-3">Satış Detayı</h3><div className="space-y-2.5 text-sm"><div className="flex justify-between"><span className="text-foreground-400">Ödeme Tipi</span><span className="text-foreground-600">{sale.payment_type}</span></div><div className="flex justify-between"><span className="text-foreground-400">Garanti</span><span className="text-foreground-600">{sale.warranty_period}</span></div><div className="flex justify-between"><span className="text-foreground-400">Personel</span><span className="text-foreground-600">{sale.created_by}</span></div></div></div>
      </div>

      {sale.notes && <div className="bg-background-50 rounded-xl border border-background-200/70 p-4"><h3 className="text-sm font-semibold text-foreground-800 mb-2">Notlar</h3><p className="text-sm text-foreground-500">{sale.notes}</p></div>}

      {/* e-Fatura / e-Arşiv Faturaları */}
      {invoices.length > 0 && (
        <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
          <div className="px-5 py-3 border-b border-background-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-foreground-800">e-Fatura / e-Arşiv Kayıtları</h3>
            <span className="text-xs text-foreground-400">{invoices.length} fatura</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr className="border-b border-background-100">
                  <th className="table-header">Belge No</th>
                  <th className="table-header">Tür</th>
                  <th className="table-header">Durum</th>
                  <th className="table-header">Gönderim Tarihi</th>
                  <th className="table-header">Ref ID</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv: any) => (
                  <tr key={inv.id} className="border-b border-background-100">
                    <td className="table-cell font-medium text-foreground-800">{inv.invoice_number || '—'}</td>
                    <td className="table-cell">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${inv.document_type === 'EINVOICE' ? 'bg-primary-100 text-primary-700' : 'bg-accent-100 text-accent-700'}`}>
                        {inv.document_type === 'EINVOICE' ? 'e-Fatura' : 'e-Arşiv'}
                      </span>
                    </td>
                    <td className="table-cell py-2"><Badge badgeClass={elogoStatus.badgeClass(inv.status)} icon={elogoStatus.icon(inv.status)}>{getElogoStatusLabel(inv.status)}</Badge></td>
                    <td className="table-cell text-foreground-400">{inv.sent_at ? new Date(inv.sent_at).toLocaleDateString('tr-TR') : '—'}</td>
                    <td className="table-cell text-foreground-400 text-xs">{inv.ref_id || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* eLogo Fatura Hazırlama Modalı */}
      {showElogoModal && (
        <div className="fixed inset-0 bg-black/40 z-[100] flex items-center justify-center p-4" onClick={() => { if (!elogoLoading) setShowElogoModal(false); }}>
          <div className="bg-background-50 rounded-2xl p-6 max-w-lg w-full shadow-xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-5">
              <span className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-file-text-line text-accent-600 text-lg"></i>
              </span>
              <div>
                <h3 className="text-lg font-semibold text-foreground-900">eLogo Test Faturası Hazırla</h3>
                <p className="text-xs text-foreground-400">Fatura taslak olarak eLogo test portalına gönderilecektir</p>
              </div>
            </div>

            {/* Satış Özeti */}
            <div className="bg-background-100 rounded-xl p-4 mb-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-foreground-400">Satış No</span><span className="font-medium text-foreground-700">{sale.sale_no}</span></div>
              <div className="flex justify-between"><span className="text-foreground-400">Müşteri</span><span className="font-medium text-foreground-700">{customer?.full_name || sale.customer_name}</span></div>
              <div className="flex justify-between"><span className="text-foreground-400">Toplam Tutar</span><span className="font-bold text-foreground-900">{formatTRY(sale.total_amount)}</span></div>
              <div className="flex justify-between"><span className="text-foreground-400">KDV</span><span className="font-medium text-foreground-700">%{sale.vat_rate || 20} — {formatTRY(sale.vat_amount || 0)}</span></div>
              <div className="flex justify-between"><span className="text-foreground-400">Ürün Sayısı</span><span className="font-medium text-foreground-700">{items.length}</span></div>
              <div className="flex justify-between"><span className="text-foreground-400">Tarih</span><span className="font-medium text-foreground-700">{new Date(sale.sale_date).toLocaleDateString('tr-TR')}</span></div>
            </div>

            {/* Müşteri Fatura Bilgileri */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
              <p className="text-xs text-amber-700 font-medium mb-1 flex items-center gap-1">
                <i className="ri-information-line"></i>
                Müşteri Fatura Bilgileri
              </p>
              {customer?.tax_number ? (
                <div className="text-xs text-amber-600 space-y-0.5">
                  <p>VKN/TCKN: {customer.tax_number}</p>
                  {customer.legal_name && <p>Ünvan: {customer.legal_name}</p>}
                  {customer.tax_office && <p>Vergi Dairesi: {customer.tax_office}</p>}
                </div>
              ) : (
                <div className="text-xs text-amber-600">
                  <p>Müşterinin fatura bilgileri (VKN/TCKN, adres) eksik.</p>
                  <button onClick={() => { setShowElogoModal(false); navigate(`/customers/${customer?.id}`); }} className="text-amber-700 underline mt-1 cursor-pointer">
                    Fatura bilgilerini tamamlamak için müşteri detayına git
                  </button>
                </div>
              )}
            </div>

            {elogoError && (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-600 mb-4 flex items-start gap-2">
                <i className="ri-error-warning-line mt-0.5 shrink-0"></i>
                <span>{elogoError}</span>
              </div>
            )}

            {elogoResult && (
              <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 mb-4">
                <p className="text-sm font-semibold text-emerald-700">Fatura taslağı oluşturuldu!</p>
                <div className="text-xs text-emerald-600 mt-1 space-y-0.5">
                  {elogoResult.refId && <p>Ref ID: {elogoResult.refId}</p>}
                  {elogoResult.invoiceUuid && <p>UUID: {elogoResult.invoiceUuid}</p>}
                  <p>Durum: Taslak olarak eLogo portalına gönderildi.</p>
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <button onClick={() => setShowElogoModal(false)} className="flex-1 btn-outline text-center cursor-pointer whitespace-nowrap">Kapat</button>
              {!elogoResult && customer?.tax_number && (
                <button
                  onClick={async () => {
                    setElogoLoading(true);
                    setElogoError(null);
                    try {
                      // Müşteri e-Fatura mükellefi mi kontrol et
                      const isCompany = customer.customer_type === 'company' || !!customer.legal_name;
                      const docType = isCompany ? 'EINVOICE' : 'EARCHIVE';
                      const alias = customer.e_invoice_alias || undefined;

                      // Gerçek fatura oluşturma - şimdilik mock
                      // TODO: UBL builder ve ZIP+MD5 implementasyonu test ortamında yapılacak
                      setElogoError('UBL/XML oluşturma ve eLogo gönderimi sonraki aşamada test edilecektir. Şu an yalnızca bağlantı testi yapılmıştır.');
                    } catch (err: any) {
                      setElogoError(err.message || 'Fatura hazırlanırken hata oluştu');
                    } finally {
                      setElogoLoading(false);
                    }
                  }}
                  disabled={elogoLoading}
                  className="flex-1 bg-accent-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {elogoLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      Hazırlanıyor...
                    </span>
                  ) : (
                    'Taslak Fatura Gönder'
                  )}
                </button>
              )}
            </div>

            <div className="border-t border-background-100 pt-3 mt-4">
              <p className="text-[10px] text-foreground-300">
                Bu işlem yalnızca eLogo TEST ortamında çalışır. Gerçek fatura kesilmez.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Değişiklik Geçmişi */}
      <div className="bg-background-50 rounded-xl border border-background-200/70 p-4">
        <button
          onClick={async () => {
            if (!showRevisions) {
              setLoadingRevisions(true);
              const bizId = await DB.getUserBusinessId();
              const { data, error: revErr } = await supabase
                .from('sale_revisions')
                .select('*')
                .eq('sale_id', id)
                .eq('business_id', bizId!)
                .order('revision_no', { ascending: false });
              if (!revErr && data) setRevisions(data);
              setLoadingRevisions(false);
            }
            setShowRevisions(!showRevisions);
          }}
          className="flex items-center justify-between w-full cursor-pointer"
        >
          <h3 className="text-sm font-semibold text-foreground-800">Değişiklik Geçmişi</h3>
          <div className="flex items-center gap-2">
            <span className="text-xs text-foreground-400">{revisions.length > 0 ? `${revisions.length} değişiklik` : ''}</span>
            <i className={showRevisions ? 'ri-arrow-up-s-line text-foreground-400' : 'ri-arrow-down-s-line text-foreground-400'}></i>
          </div>
        </button>
        {showRevisions && (
          <div className="mt-3 space-y-2">
            {loadingRevisions ? (
              <div className="flex justify-center py-4"><div className="w-5 h-5 border-2 border-primary-200 border-t-primary-500 rounded-full animate-spin"></div></div>
            ) : revisions.length === 0 ? (
              <p className="text-sm text-foreground-400 text-center py-3">Henüz bir değişiklik kaydı bulunmuyor.</p>
            ) : (
              revisions.map((rev: any) => (
                <div key={rev.id} className="bg-background-100/50 rounded-lg p-3 text-sm">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-foreground-700">Revizyon #{rev.revision_no}</span>
                    <span className="text-xs text-foreground-400">{new Date(rev.changed_at).toLocaleDateString('tr-TR')} {new Date(rev.changed_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                  <p className="text-xs text-foreground-500 mb-2">{rev.reason}</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                    <div><span className="text-foreground-400">Eski Toplam:</span> <span className="font-medium text-foreground-600">{formatTRY(rev.old_total_amount)}</span></div>
                    <div><span className="text-foreground-400">Yeni Toplam:</span> <span className="font-medium text-foreground-700">{formatTRY(rev.new_total_amount)}</span></div>
                    {rev.debt_delta != null && rev.debt_delta !== 0 && <div><span className="text-foreground-400">Borç Değişimi:</span> <span className={`font-medium ${rev.debt_delta > 0 ? 'text-red-500' : 'text-emerald-600'}`}>{rev.debt_delta > 0 ? '+' : ''}{formatTRY(rev.debt_delta)}</span></div>}
                    {rev.cost_delta != null && rev.cost_delta !== 0 && <div><span className="text-foreground-400">Maliyet Değişimi:</span> <span className="font-medium text-foreground-600">{rev.cost_delta > 0 ? '+' : ''}{formatTRY(rev.cost_delta)}</span></div>}
                  </div>
                  {rev.changed_by && <p className="text-[10px] text-foreground-300 mt-2">{rev.changed_by} tarafından</p>}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {showCancelModal && (
        <div className="fixed inset-0 bg-black/40 z-[100] flex items-center justify-center p-4" onClick={() => setShowCancelModal(false)}>
          <div className="bg-background-50 rounded-2xl p-6 max-w-sm w-full shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4 mx-auto"><i className="ri-error-warning-line text-xl text-red-500"></i></div>
            <h3 className="text-lg font-semibold text-foreground-900 text-center mb-2">Satışı İptal Et</h3>
            <p className="text-sm text-foreground-500 text-center mb-6">Bu satış iptal edilecek.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowCancelModal(false)} className="flex-1 btn-outline text-center cursor-pointer">Vazgeç</button>
              <button onClick={handleCancel} className="flex-1 bg-red-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-600 transition-colors cursor-pointer">İptal Et</button>
            </div>
          </div>
        </div>
      )}
      <PaymentFormModal
        open={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        mode="sale"
        customerId={sale?.customer_id}
        saleId={sale?.id}
        onPaymentCreated={() => { setShowPaymentModal(false); window.location.reload(); }}
      />
    </div>
  );
}